import React, { useEffect, useState } from "react";
import { CheckSquare, ExternalLink, FileDown, Globe, Loader2, Paperclip, Save, Square } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { EmailReportButton } from "./EmailReportButton";

export const OSINT_SOURCES = [
  { key: "google", label: "Google", url: (n) => `https://www.google.com/search?q=${n}` },
  { key: "linkedin", label: "LinkedIn", url: (n) => `https://www.linkedin.com/search/results/people/?keywords=${n}` },
  { key: "twitter", label: "Twitter / X", url: (n) => `https://twitter.com/search?q=${n}` },
  { key: "facebook", label: "Facebook", url: (n) => `https://www.facebook.com/search/people/?q=${n}` },
  { key: "pipl", label: "Pipl", url: (n) => `https://pipl.com/search/?q=${n}` },
  { key: "tps", label: "TruePeopleSearch", url: (n) => `https://www.truepeoplesearch.com/results?name=${n}` },
  { key: "interpol", label: "Interpol Notices", url: (n) => `https://www.interpol.int/en/How-we-work/Notices/View-Red-Notices#${n}` },
  { key: "icmp", label: "ICMP — Desaparecidos (Panamá)", url: () => "https://oic.icmp.int/index.php?w=myface&s=search&l=es&btn_submit=1&init=1&disapp_country_id=2194", fixed: true },
  { key: "mp_pa", label: "Ministerio Público Panamá — Desaparecidos", url: () => "https://ministeriopublico.gob.pa/categoria/desaparecidos/", fixed: true },
  { key: "namus_up", label: "NamUs 2.0 — Personas no identificadas", url: () => "https://www.namus.gov/UnidentifiedPersons/Search", fixed: true },
];

export function ExpedientePicker({ value, onChange, testid }) {
  const { t } = useLang();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/compliance/expedientes").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <select className="ec-input !py-1.5 text-xs" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">{t("osint.pick_exp")}</option>
      {list.map((x) => <option key={x.id} value={x.id}>{x.form_ref || x.id.slice(0, 8)} — {x.name}</option>)}
    </select>
  );
}

export default function OsintLinks({ name, searchId = null, initialReview = null }) {
  const { t } = useLang();
  const [busy, setBusy] = useState("");
  const [reviewed, setReviewed] = useState([]);
  const [notes, setNotes] = useState("");
  const [srcNotes, setSrcNotes] = useState({});
  const [expId, setExpId] = useState("");
  const clean = (name || "").trim();
  const enc = encodeURIComponent(clean);

  useEffect(() => {
    setReviewed(initialReview?.reviewed || []); setNotes(initialReview?.notes || ""); setSrcNotes(initialReview?.source_notes || {});
  }, [searchId, initialReview]);

  const toggle = (k) => setReviewed((r) => (r.includes(k) ? r.filter((x) => x !== k) : [...r, k]));
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); } };
  const save = () => run("save", async () => { await api.patch(`/sanctions/searches/${searchId}/osint`, { reviewed, notes, source_notes: srcNotes }); toast.success(t("osint.saved")); });
  const pdfPath = `/osint/pdf?name=${enc}${searchId ? `&search_id=${searchId}` : ""}`;
  const pdf = () => run("pdf", async () => {
    if (searchId) await api.patch(`/sanctions/searches/${searchId}/osint`, { reviewed, notes, source_notes: srcNotes });
    await downloadFile(pdfPath, `osint_${clean.replace(/\s+/g, "_")}.pdf`);
  });
  const attach = () => run("attach", async () => {
    if (!expId) { toast.error(t("osint.pick_exp")); return; }
    await api.patch(`/sanctions/searches/${searchId}/osint`, { reviewed, notes, source_notes: srcNotes });
    await api.post("/osint/attach", { expediente_id: expId, search_id: searchId, name: clean });
    toast.success(t("osint.attached"));
  });

  return (
    <div className="ec-card p-6 space-y-4" data-testid="osint-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Globe className="w-5 h-5 text-[#044CBC]" /> {t("osint.title")}</h2>
          <p className="text-xs text-slate-500 mt-1">{clean ? <>{t("osint.subtitle_for")} <span className="font-bold text-slate-800" data-testid="osint-name">{clean}</span></> : t("osint.subtitle_empty")}</p>
        </div>
        <EmailReportButton pdfPath={pdfPath} filename={`osint_${clean.replace(/\s+/g, "_")}.pdf`} subject={`Evidencia OSINT — ${clean}`} module="Búsqueda OSINT" summary={`${clean} — ${reviewed.length} fuente(s) revisada(s)`} testid="osint-email" beforeSend={async () => { if (searchId) await api.patch(`/sanctions/searches/${searchId}/osint`, { reviewed, notes, source_notes: srcNotes }); }} />
        <button onClick={pdf} disabled={!clean || !!busy} data-testid="osint-pdf-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C] disabled:opacity-50">
          {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {t("osint.pdf")}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {OSINT_SOURCES.map((s) => {
          const href = s.url(enc);
          const disabled = !clean && !s.fixed;
          const on = reviewed.includes(s.key);
          return (
            <div key={s.key} className={`rounded-lg border px-3 py-2 space-y-1.5 ${on ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`osint-card-${s.key}`}>
              <div className="flex items-center gap-2">
                {searchId && (
                  <button type="button" onClick={() => toggle(s.key)} data-testid={`osint-check-${s.key}`} title={t("osint.reviewed")} className={on ? "text-emerald-600" : "text-slate-400 hover:text-slate-600"}>
                    {on ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </button>
                )}
                <a href={disabled ? undefined : href} target="_blank" rel="noopener noreferrer" aria-disabled={disabled} data-testid={`osint-link-${s.key}`}
                   className={`flex-1 flex items-center justify-between gap-2 text-sm font-semibold ${disabled ? "text-slate-400 cursor-not-allowed" : "text-slate-800 hover:text-[#044CBC]"}`}>
                  <span className="truncate">{s.label}</span><ExternalLink className="w-3.5 h-3.5 shrink-0" />
                </a>
              </div>
              {searchId && on && (
                <input className="ec-input !py-1 text-xs" placeholder={t("osint.finding_ph")} value={srcNotes[s.key] || ""} maxLength={500}
                       onChange={(e) => setSrcNotes({ ...srcNotes, [s.key]: e.target.value })} data-testid={`osint-note-${s.key}`} />
              )}
            </div>
          );
        })}
      </div>
      {searchId && (
        <div className="space-y-3 pt-3 border-t border-slate-100" data-testid="osint-evidence">
          <div>
            <label className="ec-label">{t("osint.notes")} <span className="text-slate-400 normal-case">({reviewed.length}/{OSINT_SOURCES.length} {t("osint.reviewed_count")})</span></label>
            <textarea className="ec-input" rows={3} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} placeholder={t("osint.notes_ph")} data-testid="osint-notes" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={save} disabled={!!busy} data-testid="osint-save-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C] disabled:opacity-50">
              {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t("osint.save")}
            </button>
            <div className="flex items-center gap-2 ml-auto">
              <ExpedientePicker value={expId} onChange={setExpId} testid="osint-exp-select" />
              <button onClick={attach} disabled={!!busy || !expId} data-testid="osint-attach-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#16335F] text-[#16335F] text-xs font-bold hover:bg-[#EFF4FC] disabled:opacity-50 whitespace-nowrap">
                {busy === "attach" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {t("osint.attach")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
